import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../utils/authContext";
import { registerInvestor, loginInvestorAPI } from '../utils/api';
import { GoogleLogin } from "@react-oauth/google";

export default function ProtectedBlurRoute({ element: Component }) {
  const navigate = useNavigate();
  const {
    isInvestorLoggedIn,
    isStartupLoggedIn,
    loginInvestor,
    investor,
  } = useAuth();

  const [showLogin, setShowLogin] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    number: "",
    password: "",
    linkedinProfile: "",
  });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [googleUserData, setGoogleUserData] = useState(null);
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
  const [showAdditionalInfoForm, setShowAdditionalInfoForm] = useState(false);

  useEffect(() => {
    // Prevent F12 key
    const preventF12 = (e) => {
      if (e.key === "F12") {
        e.preventDefault();
        return false;
      }
    };

    // Prevent right click
    const preventRightClick = (e) => {
      if (e.button === 2) {
        e.preventDefault();
        return false;
      }
    };

    // Detect dev tools opening
    const detectDevTools = () => {
      const threshold = 160; // Width when dev tools opens
      const widthThreshold = window.outerWidth - window.innerWidth > threshold;
      const heightThreshold = window.outerHeight - window.innerHeight > threshold;

      if (widthThreshold || heightThreshold) {
        document.body.innerHTML = '<h1 style="text-align:center;margin-top:50px;">Developer tools are disabled</h1>';
        window.location.reload();
      }
    };

    // Set up event listeners
    window.addEventListener('keydown', preventF12);
    document.addEventListener('contextmenu', preventRightClick);
    window.addEventListener('resize', detectDevTools);

    // Initial check
    detectDevTools();

    // Clean up
    return () => {
      window.removeEventListener('keydown', preventF12);
      document.removeEventListener('contextmenu', preventRightClick);
      window.removeEventListener('resize', detectDevTools);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleGoogleAuth = async (credentialResponse) => {
    setGoogleLoading(true);

    try {
      const additionalData = formData.number || formData.linkedinProfile ? {
        number: formData.number,
        linkedinProfile: formData.linkedinProfile
      } : {};

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/ourinvestors/google-auth`, {
        credential: credentialResponse.credential,
        ...additionalData
      });

      const { investor: userData, token, isNewUser, message } = response.data;

      if (!userData) {
        throw new Error(message || "Authentication failed. Please try again.");
      }

      if (userData.type !== "investor") {
        throw new Error("You are not registered as an investor.");
      }

      if (isNewUser && !userData.isActive) {
        // New user - show additional info form
        setGoogleUserData(userData);
        setShowAdditionalInfo(true);
        toast.info("Please provide your phone number and LinkedIn profile to complete registration");
        return;
      }

      if (!userData.isActive) {
        // Existing user but not active
        toast.success(message || "Your account is pending admin approval.");
        setSuccess(true);
        return;
      }

      if (!token) {
        throw new Error("No token received. Please try again.");
      }

      // Login investor via AuthContext
      await loginInvestor(token, {
        ...userData,
        name: userData.name,
        email: userData.email,
        type: "investor",
        isActive: true
      });

      toast.success(message || "Login successful!");

    } catch (err) {
      console.error("Google authentication error:", err);
      toast.error(err.response?.data?.message || err.message || "Google authentication failed. Please try again.");
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleAdditionalInfoSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Update the investor with additional info
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/ourinvestors/google-auth/${googleUserData.id}`,
        {
          number: formData.number,
          linkedinProfile: formData.linkedinProfile
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data && response.data.message) {
        toast.success(response.data.message || "Registration complete! Please wait for admin approval.");
        setShowAdditionalInfo(false);
        setGoogleUserData(null);
        setFormData({
          name: "",
          email: "",
          number: "",
          password: "",
          linkedinProfile: ""
        });
        setSuccess(true);
      } else {
        throw new Error("No response data received");
      }

    } catch (err) {
      console.error("Error updating investor info:", err);
      toast.error(err.response?.data?.message || "Failed to update information. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (showLogin) {
        // LOGIN FLOW
        const res = await loginInvestorAPI({ email: formData.email, password: formData.password });
        const investor = res?.investor;
        const token = res?.token;

        if (!investor) {
          throw new Error(res?.data?.message || "Login failed. Please try again.");
        }
        if (investor.type !== "investor") {
          throw new Error("You are not registered as an investor.");
        }
        if (!investor.isActive) {
          throw new Error("Your account is not active yet. Please wait for admin approval.");
        }
        if (!token) {
          throw new Error("No token received. Please try again.");
        }

        // Login investor via AuthContext
        await loginInvestor(token, {
          ...investor,
          name: investor.name,
          email: investor.email,
          type: "investor",
          isActive: true
        });

        toast.success("Login successful!");
        setShowLogin(false);
        setFormData({
          name: "",
          email: "",
          number: "",
          password: "",
          linkedinProfile: ""
        });

      } else {
        // REGISTER FLOW
        const payload = { ...formData, type: "investor" };
        await registerInvestor({ ...formData, type: 'investor' });

        toast.success("Registration successful! Please wait for admin approval.");
        setFormData({
          name: "",
          email: "",
          number: "",
          password: "",
          linkedinProfile: ""
        });
        setSuccess(true);
        setShowLogin(true); // Switch to login after registration
      }
    } catch (err) {
      console.error("Authentication error:", err);
      toast.error(err.response?.data?.message || err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // If logged in, show the protected component
  if (isInvestorLoggedIn && investor?.type === "investor") {
    return <Component />;
  }

  // Show additional info form for Google signup
  if (showAdditionalInfo && googleUserData) {
    return (
      <div className="relative min-h-screen">
        <ToastContainer position="top-right" autoClose={3000} />

        {/* Blur content */}
        <div className="blur-lg select-none pointer-events-none" style={{ filter: "blur(16px)" }}>
          <Component />
        </div>

        {/* Additional info modal */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-[9999] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">
              Complete Your Registration
            </h2>
            <p className="text-gray-600 mb-6 text-center">
              Please provide your phone number and LinkedIn profile to complete your registration.
            </p>

            <form onSubmit={handleAdditionalInfoSubmit} className="space-y-4">
              <div>
                <label className="block text-gray-700 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="number"
                  value={formData.number}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your phone number"
                />
              </div>

              <div>
                <label className="block text-gray-700 mb-2">LinkedIn Profile URL *</label>
                <input
                  type="url"
                  name="linkedinProfile"
                  value={formData.linkedinProfile}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="https://linkedin.com/in/yourprofile"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full text-white py-2 px-4 rounded-md transition-colors font-medium ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                  }`}
              >
                {loading ? "Completing Registration..." : "Complete Registration"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Otherwise show blur and modal
  return (
    <div className="relative min-h-screen">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Blur content */}
      <div className="blur-lg select-none pointer-events-none" style={{ filter: "blur(16px)" }}>
        <Component />
      </div>

      {/* Modal overlay */}
      <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-[9999] p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-xl p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">
            {showLogin ? "Investor Login" : "Become an Investor"}
          </h2>

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {!showLogin ? (
              <>
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    name="number"
                    value={formData.number}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">Password</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={8}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="mb-4 md:col-span-2">
                  <label className="block text-gray-700 mb-2">LinkedIn URL</label>
                  <input
                    type="url"
                    name="linkedinProfile"
                    value={formData.linkedinProfile}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="mb-4 md:col-span-2">
                  <label className="block text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="mb-4 md:col-span-2">
                  <label className="block text-gray-700 mb-2">Password</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </>
            )}

            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={loading}
                className={`w-full text-white py-2 px-4 rounded-md transition-colors font-medium ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                  }`}
              >
                {loading ? (showLogin ? "Logging in..." : "Registering...") : showLogin ? "Login" : "Register"}
              </button>
            </div>

            {/* <div className="md:col-span-2 flex items-center my-2">
              <div className="flex-grow border-t border-gray-300"></div>
              <span className="mx-4 text-gray-500">or</span>
              <div className="flex-grow border-t border-gray-300"></div>
            </div> */}

            {/* <div className="md:col-span-2 flex justify-center">
              <GoogleLogin
                onSuccess={handleGoogleAuth}
                onError={() => {
                  toast.error("Google login failed. Please try again.");
                }}
                theme="filled_blue"
                size="large"
                width="100%"
                text={googleLoading ? "Signing in..." : "continue_with"}
                disabled={googleLoading}
              />
            </div> */}

            <div className="md:col-span-2 text-center">
              {!showLogin ? (
                <button
                  type="button"
                  onClick={() => {
                    setShowLogin(true);
                    setFormData((p) => ({ ...p, password: "" }));
                    setSuccess(false);
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Already registered? Login Now
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setShowLogin(false);
                    setFormData((p) => ({ ...p, password: "" }));
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Need an account? Register here
                </button>
              )}
            </div>

            {!showLogin && success && (
              <div className="md:col-span-2 mt-2">
                <p className="text-green-600 font-medium text-center">
                  ✅ Please wait until the admin approves your details.
                </p>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}